import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { Text, Date, Number, Timezone } from "../../../controls";
import Form from "react-bootstrap/Form";
import { useParams, useNavigate } from "react-router-dom";
import { Utils, Constants } from "../../../utils";
import CategoryDropdown from "../../../components/CategoryDropdown";
import SubCategoryDropdown from "../../../components/SubCategoryDropdown";
import MediumDropdown from "../../../components/MediumDropdown";
import { usePersistStore, useStore } from "../../../store";
import {
  BlobService,
  AdService,
  CategoryService,
  TrustapUserService,
} from "../../../services";
import CommonModal from "../../../components/CommonModal";
import ImageLightbox from "../../../components/ImageLightbox";
import { PutBlobResult, put } from "@vercel/blob";
import { v4 as uuid } from "uuid";
import moment from "../../../utils/moment";

function Update() {
  const { adId } = useParams();
  const navigate = useNavigate();
  const { token, user } = usePersistStore((s: any) => s);
  const [showLinkTrustap, setShowLinkTrustap] = useState(false);
  const { setInfoMessage, setErrorMessage, setSuccessMessage, setIsLoading } =
    useStore((s: any) => s);
  const [dragging, setDragging] = useState(false);
  const inputFile = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [alreadyExistingFiles, setAlreadyExistingFiles] = useState<any[]>([]);
  const [, setData] = useState<any>(null);
  const [others, setOthers] = useState<any>(null);
  const [values, setValues] = useState({
    title: "",
    categoryId: "",
    otherCategoryName: "",
    subCategoryId: "",
    otherSubCategoryName: "",
    mediumId: "",
    otherMediumName: "",
    height: "",
    width: "",
    depth: "",
    weight: "",
    price: "",
    biddingStarts: "",
    biddingEnds: "",
    timezone: user?.timezone || "",
    description: "",
  });
  const [validationMessage, setValidationMessage] = useState<string>("");
  // Tracks the biddingStarts value as it was loaded from the API. The 7-day
  // rule only applies to *new* dates the user picks; if they leave the
  // existing value alone (which may already be <7 days out for an ad
  // that's been pending review), the form must still submit cleanly.
  const [originalBiddingStarts, setOriginalBiddingStarts] = useState<string>("");

  useEffect(() => {
    if (!adId) return;
    reload(adId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adId]);

  const reload = async (adId: string) => {
    setIsLoading(true);
    let result = await AdService.getMineOne(token, { adId });
    if (result.error) {
      setErrorMessage(result.error);
      setIsLoading(false);
      return;
    }

    setData(result?.data);
    setValues({
      title: result?.data?.title || "",
      categoryId: result?.data?.categoryId?._id || "",
      otherCategoryName: result?.data?.otherCategoryName || "",
      subCategoryId: result?.data?.subCategoryId?._id || "",
      otherSubCategoryName: result?.data?.otherSubCategoryName || "",
      mediumId: result?.data?.mediumId?._id || "",
      otherMediumName: result?.data?.otherMediumName || "",
      height: result?.data?.height?.toString() || "",
      width: result?.data?.width?.toString() || "",
      depth: result?.data?.depth?.toString() || "",
      weight: result?.data?.weight?.toString() || "",
      price: result?.data?.price?.toString() || "",
      biddingStarts: result?.data?.biddingStarts || "",
      biddingEnds: result?.data?.biddingEnds || "",
      timezone: result?.data?.timezone || user?.timezone || "",
      description: result?.data?.description || "",
    });
    setOriginalBiddingStarts(result?.data?.biddingStarts || "");
    setAlreadyExistingFiles(result?.data?.images || []);
    setIsLoading(false);
  };

  const reloadOthers = useCallback(async () => {
    setIsLoading(true);
    let result = await CategoryService.others();

    if (result.error) {
      setErrorMessage(result.error);
      setIsLoading(false);
      return;
    }

    const data: any = result?.data;
    setOthers(data);
    setIsLoading(false);
  }, [setOthers, setIsLoading, setErrorMessage]);

  useEffect(() => {
    reloadOthers();
  }, [reloadOthers]);

  // Same gate as Create: seller must have a full Trustap account before
  // editing (an updated listing still sells into the same Trustap flow).
  useEffect(() => {
    if (!token) return;
    TrustapUserService.getMyTrustapAccount(token).then((result: any) => {
      const hasFullAccount = result?.data === true;
      if (!hasFullAccount) setShowLinkTrustap(true);
    });
  }, [token]);

  const handleDragIn = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragOut = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleFileChange = (e: any) => {
    const fileList = e?.target?.files;
    if (!fileList?.length) return;
    // Snapshot before clearing the input — see the matching note in
    // Create.tsx for why this needs to happen first.
    const picked = Array.from(fileList) as File[];
    if (e?.target) e.target.value = "";

    setSelectedFiles((prev: any[]) => {
      // Append to the existing selection (don't replace), respecting the
      // overall cap of MAX_FILES which counts both already-saved photos
      // and freshly-picked ones. Dedupe by name+size so the same file
      // picked twice doesn't double up.
      const room = Constants.MAX_FILES - alreadyExistingFiles.length - prev.length;
      const next = [...prev];
      const seen = new Set(next.map((f) => `${f?.name}__${f?.size}`));
      let added = 0;
      let truncated = false;
      for (const file of picked) {
        if (added >= room) {
          truncated = true;
          break;
        }
        const key = `${file?.name}__${file?.size}`;
        if (seen.has(key)) continue;
        seen.add(key);
        next.push({
          img: URL.createObjectURL(file),
          name: file?.name,
          size: file?.size,
          file,
        });
        added++;
      }
      if (truncated) {
        setInfoMessage(`You can choose upto ${Constants.MAX_FILES} images.`);
      }
      return next;
    });
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    handleFileChange({ target: { files } });
  };

  const handleRemoveImage = (e: any, name: string) => {
    e?.preventDefault();
    e?.stopPropagation();
    let t: any[] = selectedFiles?.filter(
      (file: any) => file?.name?.toLowerCase() !== name?.toLowerCase()
    );
    setSelectedFiles(t);
  };

  const handleDeleteExistingImage = async (e: any, url: string) => {
    if (!adId) return;
    e?.preventDefault();
    e?.stopPropagation();

    let t: string[] = alreadyExistingFiles?.filter(
      (file: string) => file !== url
    );
    setAlreadyExistingFiles(t);

    setIsLoading(true);
    let result = await AdService.deleteAdImage(token, { adId: adId, key: url });
    if (!result?.error) {
      setErrorMessage(result?.error);
      setIsLoading(false);
      return;
    }

    setSuccessMessage("Listing image is removed successfully.");
    setIsLoading(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  useEffect(() => {
    setInfoMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(
    async (e: any) => {
      if (!adId) return;
      e.preventDefault();
      setValidationMessage("");
      if (selectedFiles?.length + alreadyExistingFiles?.length === 0)
        return setValidationMessage("Please provide at least one image.");
      if (selectedFiles?.length + alreadyExistingFiles?.length > 5)
        return setValidationMessage("You can choose maximum 5 images.");
      if (!values?.title)
        return setValidationMessage("Please provide a title.");
      if (!values?.categoryId)
        return setValidationMessage("Please choose a category.");
      if (!values?.subCategoryId)
        return setValidationMessage("Please choose a sub category.");
      if (!values?.mediumId)
        return setValidationMessage(
          "Please choose the medium of this art work."
        );
      if (!values?.height)
        return setValidationMessage("Please provide the height.");
      if (!values?.width)
        return setValidationMessage("Please provide the width.");
      if (!values?.depth)
        return setValidationMessage("Please provide the depth.");
      if (!values?.weight)
        return setValidationMessage("Please provide the weight.");
      if (!values?.price)
        return setValidationMessage("Please provide the price.");
      if (parseFloat(values?.price) < Constants.ARTWORK_MIN_PRICE)
        return setValidationMessage(
          `Minimum price should be ${Utils.formatToCurrency(
            Constants.ARTWORK_MIN_PRICE.toString()
          )}.`
        );
      if (!values?.biddingStarts)
        return setValidationMessage("Please provide bid opening date.");
      if (!values?.biddingEnds)
        return setValidationMessage("Please provide bid closing date.");
      // Only enforce the 7-day rule when the user actually picked a new
      // biddingStarts. Editing other fields on an ad whose existing window
      // is already <7 days away (e.g. pending review for several days)
      // must keep working without forcing the seller to reschedule.
      if (
        values?.biddingStarts !== originalBiddingStarts &&
        moment(values?.biddingStarts).isBefore(
          moment().add(Constants.BIDDING_STARTS_MIN_DAYS_AHEAD, "days").startOf("day")
        )
      )
        return setValidationMessage(
          `Bid opens date must be at least ${Constants.BIDDING_STARTS_MIN_DAYS_AHEAD} days from today.`
        );
      if (
        moment(values?.biddingStarts).isSameOrAfter(moment(values?.biddingEnds))
      )
        return setValidationMessage(
          "Bid ends date should be after than bid opens date."
        );
      if (
        moment(values?.biddingEnds).isSameOrAfter(
          moment(values?.biddingStarts).add(3, "months")
        )
      )
        return setValidationMessage(
          "Bid ends date must be within 3 months of bid opens date."
        );
      if (!values?.timezone)
        return setValidationMessage("Please select a timezone.");
      if (!values?.description)
        return setValidationMessage("Please provide a description.");

      let files = selectedFiles?.map((k: any) => k?.file);
      setIsLoading(true);

      let images: string[] = [];
      for (let i = 0; i < files?.length; i++) {
        let file = files[i];
        const ext = Utils.getFileExtension(file?.name);
        const filename = `${uuid()}.${ext}`;
        const tokenResult = await BlobService.getClientToken(token, {
          filename,
        });
        if (tokenResult.error) {
          setErrorMessage(tokenResult.error);
          setIsLoading(false);
          return;
        }

        const clientToken = tokenResult?.data?.token || false;
        if (!clientToken) {
          setErrorMessage("Failed to upload image. Please try again.");
          setIsLoading(false);
          return;
        }

        const blob: PutBlobResult = await put(filename, file, {
          access: "public",
          token: clientToken,
        });
        if (!blob) {
          setErrorMessage("Failed to upload image. Please try again.");
          setIsLoading(false);
          return;
        }

        images.push(blob?.url);
      }

      let result = await AdService.update(token, adId, {
        ...values,
        images: images,
      });
      if (result.error) {
        setErrorMessage(result.error);
        setIsLoading(false);
        return;
      }

      setSuccessMessage("Listing is updated successfully.");
      setIsLoading(false);
    },
    [
      adId,
      values,
      selectedFiles,
      alreadyExistingFiles,
      setErrorMessage,
      setIsLoading,
      setSuccessMessage,
      token,
    ]
  );

  return (
    <>
      <CommonModal
        title="Link your Trustap account"
        message="You need to link your Trustap account before editing a listing. Without it, you won't be able to receive payouts when your artwork sells."
        show={showLinkTrustap}
        onHide={() => {
          setShowLinkTrustap(false);
          navigate("/dashboard/trustap-account");
        }}
      />
      <div className="bg-pink p-4 createListingMain">
        <div className="container-fluid px-0">
          <div className="row">
            <div className="col-12 col-xl-9 mx-auto">
              {/* Page header */}
              <div className="tableCardHeader px-0 mb-4">
                <div className="tableCardHeader__titleBlock">
                  <h2 className="tableCardHeader__title m-0">Update your ad</h2>
                  <p className="tableCardHeader__subtitle m-0">
                    Tweak the details, photos, or auction window before saving.
                  </p>
                </div>
              </div>

              {/* Section: Photos */}
              <div className="formSection">
                <div className="formSection__header">
                  <div className="formSection__iconWrap">
                    <i className="bi bi-images" aria-hidden="true" />
                  </div>
                  <div className="formSection__titleBlock">
                    <span className="formSection__kicker">Step 1</span>
                    <h3 className="formSection__title">Photos</h3>
                    <p className="formSection__subtitle">
                      Up to {Constants.MAX_FILES} total. Existing photos can be
                      removed individually.
                    </p>
                  </div>
                </div>

                <div
                  className={`uploadZone ${dragging ? "uploadZone--dragging" : ""}`}
                  onDragEnter={handleDragIn}
                  onDragOver={handleDragIn}
                  onDragLeave={handleDragOut}
                  onDrop={handleDrop}
                  onClick={() => inputFile.current?.click()}
                  role="button"
                  tabIndex={0}
                >
                  <span className="uploadZone__icon">
                    <i className="bi bi-cloud-arrow-up" aria-hidden="true" />
                  </span>
                  <h4 className="uploadZone__title">
                    Drag &amp; drop new images here
                  </h4>
                  <p className="uploadZone__hint">
                    PNG or JPG ·{" "}
                    {selectedFiles.length + alreadyExistingFiles.length} of{" "}
                    {Constants.MAX_FILES} total
                  </p>
                  <button
                    type="button"
                    className="uploadZone__browseBtn"
                    onClick={(e) => {
                      e.stopPropagation();
                      inputFile.current?.click();
                    }}
                  >
                    <i className="bi bi-folder2-open" aria-hidden="true" />
                    <span>Browse files</span>
                  </button>
                  <input
                    id="txt-listing-pictures"
                    type="file"
                    accept="image/png, image/jpg, image/jpeg"
                    style={{ display: "none" }}
                    ref={inputFile}
                    multiple
                    onChange={handleFileChange}
                  />
                </div>

                {alreadyExistingFiles?.length > 0 && (
                  <>
                    <div className="formSection__kicker mt-4 mb-2">
                      Existing photos
                    </div>
                    <div className="uploadGrid">
                      {alreadyExistingFiles.map((file, index) => (
                        <div
                          key={`existing-${index}`}
                          className="uploadGrid__item"
                          role="button"
                          tabIndex={0}
                          aria-label={`Preview existing image ${index + 1}`}
                          onClick={() => setLightboxSrc(file)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setLightboxSrc(file);
                            }
                          }}
                        >
                          <img
                            src={file}
                            alt={`Existing ${index + 1}`}
                            className="uploadGrid__img"
                          />
                          <button
                            type="button"
                            className="uploadGrid__remove"
                            aria-label="Remove this image"
                            onClick={(e) => handleDeleteExistingImage(e, file)}
                          >
                            <i className="bi bi-x-lg" aria-hidden="true" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {selectedFiles?.length > 0 && (
                  <>
                    <div className="formSection__kicker mt-4 mb-2">
                      Pending uploads
                    </div>
                    <div className="uploadGrid">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={`new-${index}`}
                          className="uploadGrid__item"
                          role="button"
                          tabIndex={0}
                          aria-label={`Preview ${file?.name}`}
                          onClick={() => setLightboxSrc(file?.img)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setLightboxSrc(file?.img);
                            }
                          }}
                        >
                          <img
                            src={file?.img}
                            alt={`Pending ${index + 1}`}
                            className="uploadGrid__img"
                          />
                          <button
                            type="button"
                            className="uploadGrid__remove"
                            aria-label={`Remove ${file?.name}`}
                            onClick={(e) => handleRemoveImage(e, file?.name)}
                          >
                            <i className="bi bi-x-lg" aria-hidden="true" />
                          </button>
                          <span
                            className="uploadGrid__name"
                            title={file?.name}
                          >
                            {file?.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Section: Basics */}
              <div className="formSection">
                <div className="formSection__header">
                  <div className="formSection__iconWrap">
                    <i className="bi bi-pencil-square" aria-hidden="true" />
                  </div>
                  <div className="formSection__titleBlock">
                    <span className="formSection__kicker">Step 2</span>
                    <h3 className="formSection__title">Basics</h3>
                    <p className="formSection__subtitle">
                      Title, category, and description.
                    </p>
                  </div>
                </div>

                <Text
                  name="title"
                  label="Title"
                  placeholder="A sky beneath the stars"
                  value={values?.title}
                  onChange={handleInputChange}
                  required
                />

                <div className="row">
                  <div className="col-md-6">
                    <div className="field mb-3">
                      <label className="field__label">
                        Category<span className="field__required">*</span>
                      </label>
                      <CategoryDropdown
                        name="categoryId"
                        value={values?.categoryId}
                        onChange={handleInputChange}
                      />
                      {values?.categoryId === others?.category?._id && (
                        <Text
                          name="otherCategoryName"
                          placeholder="Enter category name"
                          value={values?.otherCategoryName}
                          onChange={handleInputChange}
                          parentStyles={{ marginTop: 8 }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="field mb-3">
                      <label className="field__label">
                        Sub Category<span className="field__required">*</span>
                      </label>
                      <SubCategoryDropdown
                        categoryId={values?.categoryId}
                        name="subCategoryId"
                        value={values?.subCategoryId}
                        onChange={handleInputChange}
                        onClick={(e: any) => {
                          e?.preventDefault();
                          if (!values?.categoryId) {
                            setInfoMessage(
                              "Please choose a category first."
                            );
                          }
                        }}
                      />
                      {values?.subCategoryId === others?.subCategory?._id && (
                        <Text
                          name="otherSubCategoryName"
                          placeholder="Enter sub category name"
                          value={values?.otherSubCategoryName}
                          onChange={handleInputChange}
                          parentStyles={{ marginTop: 8 }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="field mb-3">
                  <label className="field__label">
                    Medium<span className="field__required">*</span>
                  </label>
                  <MediumDropdown
                    categoryId={values?.categoryId}
                    name="mediumId"
                    value={values?.mediumId}
                    onChange={handleInputChange}
                    onClick={(e: any) => {
                      e?.preventDefault();
                      if (!values?.categoryId) {
                        setInfoMessage("Please choose a category first.");
                      }
                    }}
                  />
                  {values?.mediumId === others?.medium?._id && (
                    <Text
                      name="otherMediumName"
                      placeholder="Enter medium name"
                      value={values?.otherMediumName}
                      onChange={handleInputChange}
                      parentStyles={{ marginTop: 8 }}
                    />
                  )}
                </div>

                <Form.Group className="field mb-0">
                  <label className="field__label">
                    Description<span className="field__required">*</span>
                  </label>
                  <Form.Control
                    name="description"
                    as="textarea"
                    rows={5}
                    placeholder="Tell collectors about the inspiration, technique, story behind this piece…"
                    value={values?.description}
                    onChange={handleInputChange}
                  />
                  <div className="field__meta">
                    <span className="field__hint">
                      {values?.description
                        ? `${values.description.length} characters`
                        : "Aim for 80–300 characters."}
                    </span>
                  </div>
                </Form.Group>
              </div>

              {/* Section: Specifications */}
              <div className="formSection">
                <div className="formSection__header">
                  <div className="formSection__iconWrap">
                    <i className="bi bi-rulers" aria-hidden="true" />
                  </div>
                  <div className="formSection__titleBlock">
                    <span className="formSection__kicker">Step 3</span>
                    <h3 className="formSection__title">Specifications</h3>
                    <p className="formSection__subtitle">
                      Used for shipping quotes and the buyer's specs panel.
                    </p>
                  </div>
                </div>

                <label className="field__label">
                  Dimensions<span className="field__required">*</span>
                </label>
                <div className="dimensionsRow mb-3">
                  <Number
                    name="height"
                    value={values?.height}
                    onChange={handleInputChange}
                    suffix=" in"
                    thousandSeparator={true}
                    placeholder="Height"
                  />
                  <span className="dimensionsRow__sep">×</span>
                  <Number
                    name="width"
                    value={values?.width}
                    onChange={handleInputChange}
                    suffix=" in"
                    thousandSeparator={true}
                    placeholder="Width"
                  />
                  <span className="dimensionsRow__sep">×</span>
                  <Number
                    name="depth"
                    value={values?.depth}
                    onChange={handleInputChange}
                    suffix=" in"
                    thousandSeparator={true}
                    placeholder="Depth"
                  />
                </div>

                <Number
                  name="weight"
                  label="Weight"
                  placeholder="0"
                  value={values?.weight}
                  onChange={handleInputChange}
                  suffix=" lbs"
                  thousandSeparator={true}
                  required
                />
              </div>

              {/* Section: Pricing & schedule */}
              <div className="formSection">
                <div className="formSection__header">
                  <div className="formSection__iconWrap">
                    <i className="bi bi-cash-coin" aria-hidden="true" />
                  </div>
                  <div className="formSection__titleBlock">
                    <span className="formSection__kicker">Step 4</span>
                    <h3 className="formSection__title">
                      Pricing &amp; schedule
                    </h3>
                    <p className="formSection__subtitle">
                      Opening bid and the auction window.
                    </p>
                  </div>
                </div>

                <Number
                  name="price"
                  label="Opening bid price"
                  placeholder="0"
                  value={values?.price}
                  onChange={handleInputChange}
                  prefix="$"
                  thousandSeparator={true}
                  required
                  hint={`Minimum amount: ${Utils.formatToCurrency(
                    Constants.ARTWORK_MIN_PRICE.toString()
                  )}`}
                />

                <Timezone
                  name="timezone"
                  label="Timezone"
                  value={values?.timezone}
                  onChange={handleInputChange}
                />

                <div className="row">
                  <div className="col-md-6">
                    <Date
                      name="biddingStarts"
                      label="Bid opens on"
                      placeholder="Pick a date & time"
                      value={values?.biddingStarts}
                      onChange={handleInputChange}
                      showTimeInput={true}
                      hint={`Shown in your local timezone. New picks must be at least ${Constants.BIDDING_STARTS_MIN_DAYS_AHEAD} days from today.`}
                      minDate={moment()
                        .add(Constants.BIDDING_STARTS_MIN_DAYS_AHEAD, "days")
                        .startOf("day")
                        .toDate()}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <Date
                      name="biddingEnds"
                      label="Bid ends on"
                      placeholder="Pick a date & time"
                      value={values?.biddingEnds}
                      onChange={handleInputChange}
                      showTimeInput={true}
                      hint={Utils.formatBidEndsHint(
                        values?.biddingEnds,
                        values?.timezone
                      )}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Sticky action bar */}
              <div className="formStickyBar">
                {validationMessage ? (
                  <span className="formStickyBar__msg formStickyBar__msg--error">
                    <i
                      className="bi bi-exclamation-circle"
                      aria-hidden="true"
                    />
                    {validationMessage}
                  </span>
                ) : (
                  <span className="formStickyBar__msg">
                    Changes are saved when you click Update.
                  </span>
                )}
                <div className="formStickyBar__actions">
                  <button
                    type="button"
                    className="placeBidModal__cancel"
                    onClick={() => navigate("/dashboard/listing")}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="placeBidModal__submit"
                    onClick={handleSubmit}
                  >
                    <i className="bi bi-check2-circle" aria-hidden="true" />
                    <span>Update listing</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ImageLightbox
        src={lightboxSrc}
        onClose={() => setLightboxSrc(null)}
      />
    </>
  );
}

export default Update;
